import { Component, HostListener, NgZone, OnDestroy, OnInit } from "@angular/core";
import { nativeAppUrl, androidAppDownloadUrl, iosAppDownloadUrl } from "../../shared/utils/native-app-url.utils";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { timeout } from "rxjs/operators";
import {
  GuestOwnerEventRequest,
  GuestOwnerEventResponse,
  GuestOwnerInviteResponse,
  GuestOwnerParticipantDto,
  GuestOwnerPlayerDto,
  GuestOwnerSessionState,
  PublicEventInviteChatMessageResponse
} from "../../shared/models/guestOwner";
import { GuestOwnerService } from "../../shared/services/guest-owner.service";
import { GuestOwnerSessionService } from "../../shared/services/guest-owner-session.service";
import { GuestUpgradeClaimService } from "../../shared/services/guest-upgrade-claim.service";
import { GuestContinuationObserverService } from "../../shared/services/guest-continuation-observer.service";
import { Subscription } from "rxjs";
import { GuestParticipantListItem } from "../guest-flow/guest-participant-list.component";

interface GuestOwnerForm {
  managerName: string;
  eventName: string;
  startDateTime: string;
  startDate: string;
  startTime: string;
  maxPlayers: number;
  playSpaceName: string;
  guestName: string;
}

type GuestOwnerEditorMode = "name" | "time" | "location" | "manager";
type GuestOwnerProfileBridgeMode = "default" | "location";

@Component({
    selector: "app-guest-owner",
    templateUrl: "./guest-owner.component.html",
    styleUrls: ["./guest-owner.component.css"],
    standalone: false
})
export class GuestOwnerComponent implements OnInit, OnDestroy {
  private static readonly ANDROID_APP_URL = androidAppDownloadUrl;
  private static readonly IOS_APP_URL = iosAppDownloadUrl;
  private static readonly DEFAULT_MANAGER_NAMES = new Set(["organizator", "organizer"]);

  public session: GuestOwnerSessionState;
  public events: GuestOwnerEventResponse[] = [];
  public selectedEvent: GuestOwnerEventResponse;
  public participants: GuestOwnerParticipantDto[] = [];
  public invite: GuestOwnerInviteResponse;
  public inviteUrl = "";
  public form: GuestOwnerForm = {
    managerName: "",
    eventName: "",
    startDateTime: "",
    startDate: "",
    startTime: "",
    maxPlayers: 10,
    playSpaceName: "",
    guestName: ""
  };
  public loading = false;
  public errorMsg: string;
  public successMsg: string;
  public participantsRealtimeAlive = false;
  public participantsRealtimeError = false;
  public participantActions: {[participantId: string]: boolean} = {};
  public chatMessages: PublicEventInviteChatMessageResponse[] = [];
  public chatMessagesLoading = false;
  public chatMessagesUnavailable = false;
  public chatBridgeVisible = false;
  public profileBridgeVisible = false;
  public profileBridgeMode: GuestOwnerProfileBridgeMode = "default";
  public editorMode: GuestOwnerEditorMode;
  public selectedParticipant: GuestOwnerParticipantDto;
  public selectedParticipantName = "";
  public confirmDeleteVisible = false;
  public noticeTitle: string;
  public noticeMessage: string;
  public shareDialogVisible = false;
  public inviteCopied = false;
  public confirmCreateVisible = false;
  public autoCreateRequested = false;
  public setupInProgress = false;
  public openingMobileApp = false;
  public guestContinuationClaimed = false;
  public guestContinuationObserverError = false;
  public restoringGuestContinuation = false;
  private closeParticipantsStream: () => void;
  private guestContinuationObservation: Subscription;
  private participantsStreamReconnectTimer: number;
  private participantsRequestSequence = 0;
  private setupWatchdogTimer: number;
  private pendingDraftName = "";
  private destroyed = false;

  constructor(
    private guestOwnerService: GuestOwnerService,
    private sessionService: GuestOwnerSessionService,
    private guestUpgradeClaimService: GuestUpgradeClaimService,
    private guestContinuationObserverService: GuestContinuationObserverService,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private translate: TranslateService
  ) {}

  public ngOnInit(): void {
    this.destroyed = false;
    this.sessionService.getInstallationId();
    this.session = this.sessionService.getSession();
    this.pendingDraftName = (this.route.snapshot.queryParamMap.get("draftName") || "").trim();
    this.autoCreateRequested = this.route.snapshot.queryParamMap.get("autoCreate") === "true";

    if (this.session) {
      this.form.managerName = this.session.player && (this.session.player.nickName || this.session.player.name) || "";
      if (this.session.eventId && this.guestUpgradeClaimService.hasContinuationObserver("GUEST_OWNER", this.session.eventId)) {
        this.restoreGuestContinuation(this.session.eventId);
        return;
      }
      if (this.autoCreateRequested) {
        this.form.eventName = this.pendingDraftName || this.form.eventName;
        this.confirmCreateVisible = true;
        return;
      }
      this.beginSetupProgress();
      this.loadCurrentEvent();
    } else {
      if (this.autoCreateRequested) {
        this.form.eventName = this.pendingDraftName || this.form.eventName;
        this.confirmCreateVisible = true;
        return;
      }
      const managerName = (this.route.snapshot.queryParamMap.get("name") || "").trim();
      if (managerName) {
        this.form.managerName = managerName;
        this.beginSetupProgress();
        this.bootstrap();
        return;
      }

      this.router.navigate(["/"]);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.stopSetupWatchdog();
    this.stopParticipantsStream();
    this.stopGuestContinuationObserver();
  }

  public showSetupProgress(): boolean {
    return this.setupInProgress && !this.selectedEvent && !this.errorMsg;
  }

  public bootstrap(): void {
    const managerName = (this.form.managerName || "").trim();
    if (!managerName) {
      this.showNotice("guestOwner.notices.missingNameTitle", "guestOwner.notices.managerNameRequired");
      return;
    }

    this.loading = true;
    this.beginSetupProgress();
    this.clearMessages();
    this.guestOwnerService.bootstrap(managerName).pipe(timeout(6000)).subscribe(
      (response) => {
        if (this.destroyed) {
          return;
        }
        this.session = this.sessionService.saveBootstrap(response);
        this.loading = false;
        this.successMsg = this.t("guestOwner.messages.sessionReady");
        this.loadCurrentEvent();
      },
      (error) => this.handleError(error)
    );
  }

  public bootstrapAndCreateDraft(): void {
    this.loading = true;
    this.beginSetupProgress();
    this.clearMessages();
    this.guestOwnerService.bootstrap(this.form.managerName).pipe(timeout(6000)).subscribe(
      (response) => {
        if (this.destroyed) {
          return;
        }
        this.session = this.sessionService.saveBootstrap(response);
        this.form.managerName = this.session.player && (this.session.player.nickName || this.session.player.name) || "";
        this.createDraftEvent(this.pendingDraftName || this.form.eventName || this.t("events.newMatch"));
      },
      (error) => this.handleError(error)
    );
  }

  public selectEvent(event: GuestOwnerEventResponse): void {
    if (this.destroyed) {
      return;
    }

    this.selectedEvent = event;
    this.session = this.sessionService.saveCurrentEvent(event.id, event.name) || this.session;
    this.loading = false;
    this.finishSetupProgress();
    this.invite = null;
    this.inviteUrl = "";
    this.form.eventName = event.name;
    this.applyDateTimeToForm(event.startDateTime);
    this.form.maxPlayers = event.maxPlayers || 10;
    this.form.playSpaceName = event.locationName || (event.playSpace ? event.playSpace.name : "");
    this.chatMessages = [];
    this.chatMessagesLoading = false;
    this.chatMessagesUnavailable = false;
    this.loadParticipants(event.id);
    this.startParticipantsStream(event.id);
    this.startGuestContinuationObserver(event.id);
  }

  public newEvent(): void {
    this.stopParticipantsStream();
    this.selectedEvent = null;
    this.participants = [];
    this.chatMessages = [];
    this.chatMessagesUnavailable = false;
    this.invite = null;
    this.inviteUrl = "";
    this.form.eventName = "";
    this.form.startDateTime = "";
    this.form.startDate = "";
    this.form.startTime = "";
    this.form.playSpaceName = "";
    this.form.maxPlayers = 10;
  }

  public saveEvent(): void {
    if (!this.session) {
      this.showNotice("guestOwner.notices.sessionUnavailableTitle", "guestOwner.notices.sessionUnavailableMessage");
      return;
    }

    const request = this.buildEventRequest();
    if (!request) {
      return;
    }

    if (!this.selectedEvent) {
      this.confirmCreateVisible = true;
      return;
    }

    this.saveEventRequest(request);
  }

  public cancelCreateMatch(): void {
    this.confirmCreateVisible = false;
    if (this.autoCreateRequested) {
      this.autoCreateRequested = false;
      this.pendingDraftName = "";
      this.router.navigate(["/"], { replaceUrl: true });
    }
  }

  public confirmCreateMatch(): void {
    this.confirmCreateVisible = false;

    if (this.autoCreateRequested && !this.session) {
      this.beginSetupProgress();
      this.bootstrapAndCreateDraft();
      return;
    }

    if (this.autoCreateRequested && this.session) {
      this.beginSetupProgress();
      this.loadCurrentEvent();
      return;
    }

    const request = this.buildEventRequest();
    if (!request) {
      return;
    }

    this.saveEventRequest(request);
  }

  private saveEventRequest(request: GuestOwnerEventRequest): void {
    if (!this.session) {
      this.showNotice("guestOwner.notices.sessionUnavailableTitle", "guestOwner.notices.sessionUnavailableMessage");
      return;
    }

    this.loading = true;
    this.clearMessages();
    const saveRequest = this.selectedEvent
      ? this.guestOwnerService.updateEvent(this.selectedEvent.id, request)
      : this.guestOwnerService.createEvent(request);

    saveRequest.subscribe(
      (event) => {
        this.selectedEvent = event;
        this.session = this.sessionService.saveCurrentEvent(event.id, event.name) || this.session;
        this.successMsg = this.t("guestOwner.messages.saved");
        this.loadParticipants(event.id);
        this.startParticipantsStream(event.id);
        this.finishAction();
      },
      (error) => this.handleEventSaveError(error)
    );
  }

  public createInvite(showShareDialog = false): void {
    if (!this.selectedEvent) {
      this.showNotice("guestOwner.notices.saveTeamFirstTitle", "guestOwner.notices.saveBeforeInvite");
      return;
    }

    this.loading = true;
    this.clearMessages();
    this.guestOwnerService.invite(this.selectedEvent.id).subscribe(
      (invite) => {
        this.invite = invite;
        this.inviteUrl = this.guestOwnerService.publicInviteUrl(invite);
        this.sessionService.saveInvite(invite.inviteToken, invite.invitePath);
        this.successMsg = this.t("guestOwner.messages.inviteReady");
        this.shareDialogVisible = showShareDialog;
        this.finishAction();
      },
      (error) => this.handleError(error)
    );
  }

  public openShareDialog(): void {
    if (this.inviteUrl) {
      this.shareDialogVisible = true;
      return;
    }

    this.createInvite(true);
  }

  public closeShareDialog(): void {
    this.shareDialogVisible = false;
    this.inviteCopied = false;
  }

  public addGuestPlayer(): void {
    const inputAtSubmission = document.activeElement;
    const guestName = (this.form.guestName || "").trim();
    if (!guestName) {
      this.showNotice("guestOwner.notices.missingNameTitle", "guestOwner.notices.guestNameRequired");
      return;
    }

    if (!this.selectedEvent) {
      this.showNotice("guestOwner.notices.saveTeamFirstTitle", "guestOwner.notices.saveBeforePlayers");
      return;
    }

    this.loading = true;
    this.clearMessages();
    this.guestOwnerService.addParticipant(this.selectedEvent.id, { name: guestName, response: "COMING" }).subscribe(
      (participant) => {
        if (this.destroyed) {
          return;
        }
        this.form.guestName = "";
        // Dismiss only the field used for this submission, not a different field focused meanwhile.
        if (inputAtSubmission instanceof HTMLInputElement && document.activeElement === inputAtSubmission) inputAtSubmission.blur();
        this.successMsg = this.t("guestOwner.messages.playerAdded");
        this.upsertParticipant(Object.assign({}, participant, {
          participantId: participant.participantId || participant.playerId,
          playerId: participant.playerId || participant.participantId,
          matchOwner: false,
          source: participant.source || "MANAGER"
        }));
        this.loadParticipants(this.selectedEvent.id);
        this.finishAction();
      },
      (error) => this.handleError(error)
    );
  }

  public copyInvite(): void {
    if (!this.inviteUrl) {
      return;
    }

    this.inviteCopied = false;
    navigator.clipboard.writeText(this.inviteCopyText()).then(
      () => this.ngZone.run(() => {
        this.inviteCopied = true;
      }),
      () => this.ngZone.run(() => this.showNotice("guestOwner.share.title", this.inviteUrl))
    );
  }

  public inviteCopyText(): string {
    return this.t("guestOwner.share.copyText", {
      matchName: this.selectedEvent && this.selectedEvent.name ? this.selectedEvent.name : this.t("events.newMatch"),
      inviteUrl: this.inviteUrl
    });
  }

  public createAccount(): void {
    this.closeProfileBridge();
    this.closeChatBridge();
    this.router.navigate(["/guest-upgrade"], {
      queryParams: {
        mode: "guest-owner",
        eventId: this.selectedEvent && this.selectedEvent.id
      }
    });
  }

  public openProfileBridge(mode: GuestOwnerProfileBridgeMode = "default"): void {
    this.profileBridgeMode = mode;
    this.profileBridgeVisible = true;
  }

  public closeProfileBridge(): void {
    this.profileBridgeVisible = false;
    this.profileBridgeMode = "default";
  }

  public profileBridgeTitle(): string {
    return this.profileBridgeMode === "location"
      ? this.t("guestOwner.profileDialog.locationTitle")
      : this.t("guestOwner.profileDialogTitle");
  }

  public profileBridgeDescription(): string {
    return this.profileBridgeMode === "location"
      ? this.t("guestOwner.profileDialog.locationDescription")
      : this.t("guestOwner.profileDialog.description");
  }

  public profileBridgeAction(): string {
    return this.profileBridgeMode === "location"
      ? this.t("guestOwner.profileDialog.locationAction")
      : this.t("guestOwner.createProfile");
  }

  public openEditor(mode: GuestOwnerEditorMode): void {
    this.clearMessages();
    if (mode === "manager") {
      this.form.managerName = this.managerDisplayName();
    }
    this.editorMode = mode;
  }

  public closeEditor(): void {
    this.editorMode = null;
    if (this.selectedEvent) {
      this.form.eventName = this.selectedEvent.name;
      this.applyDateTimeToForm(this.selectedEvent.startDateTime);
      this.form.playSpaceName = this.selectedEvent.locationName || (this.selectedEvent.playSpace ? this.selectedEvent.playSpace.name : "");
    }
    if (this.session && this.session.player) {
      this.form.managerName = this.session.player.nickName || this.session.player.name || "";
    }
  }

  public editorTitle(): string {
    if (this.editorMode === "time") {
      return this.t("events.editMatchTime");
    }
    if (this.editorMode === "location") {
      return this.t("guestOwner.editor.locationTitle");
    }
    if (this.editorMode === "manager") {
      return this.t("guestOwner.managerNamePrompt");
    }
    return this.t("guestOwner.teamName");
  }

  public saveEditor(): void {
    if (this.editorMode === "manager") {
      this.saveManagerName();
      return;
    }

    this.saveEvent();
  }

  public suggestTeamName(): void {
    this.form.eventName = this.t("guestOwner.editor.suggestedTeamName");
  }

  public updateEditorDate(date: string): void {
    this.form.startDate = date || "";
    if (this.form.startDate && !this.form.startTime) {
      this.form.startTime = "19:00";
    }
    this.syncStartDateTimeFromParts();
  }

  public updateEditorTime(time: string): void {
    this.form.startTime = time || "";
    if (this.form.startTime && !this.form.startDate) {
      this.form.startDate = this.localDateInputValue(new Date());
    }
    this.syncStartDateTimeFromParts();
  }

  public hasEditorTime(): boolean {
    return Boolean(this.form.startDate || this.form.startTime || this.form.startDateTime);
  }

  public hasEditorLocation(): boolean {
    return Boolean((this.form.playSpaceName || "").trim());
  }

  public removeTime(): void {
    this.form.startDateTime = "";
    this.form.startDate = "";
    this.form.startTime = "";
  }

  public openChatBridge(): void {
    this.chatBridgeVisible = true;
  }

  public closeChatBridge(): void {
    this.chatBridgeVisible = false;
  }

  public openParticipantSheet(participant: GuestOwnerParticipantDto): void {
    this.selectedParticipant = participant;
    this.selectedParticipantName = participant && participant.name ? participant.name : "";
  }

  public closeParticipantSheet(): void {
    this.selectedParticipant = null;
    this.selectedParticipantName = "";
  }

  public updateSelectedParticipantResponse(response: "COMING" | "NOT_COMING"): void {
    if (!this.selectedParticipant) {
      return;
    }

    const participant = this.selectedParticipant;
    this.closeParticipantSheet();
    this.updateParticipantResponse(participant, response);
  }

  public saveSelectedParticipantName(): void {
    if (!this.selectedParticipant || this.selectedParticipant.matchOwner) {
      return;
    }

    const participantName = (this.selectedParticipantName || "").trim();
    if (!participantName) {
      this.showNotice("guestOwner.notices.missingNameTitle", "guestOwner.notices.guestNameRequired");
      return;
    }

    const participant = this.selectedParticipant;
    this.closeParticipantSheet();
    this.updateParticipantName(participant, participantName);
  }

  public removeSelectedParticipant(): void {
    if (!this.selectedParticipant) {
      return;
    }

    const participant = this.selectedParticipant;
    this.closeParticipantSheet();
    this.removeParticipant(participant);
  }

  public returnToLanding(): void {
    this.loading = false;
    this.autoCreateRequested = false;
    this.pendingDraftName = "";
    this.errorMsg = null;
    this.finishSetupProgress();
    this.stopParticipantsStream();
    this.router.navigate(["/"]);
  }

  public updateParticipantResponse(participant: GuestOwnerParticipantDto, response: "COMING" | "NOT_COMING"): void {
    const participantId = this.participantManagementId(participant);
    if (!this.selectedEvent || !participant || !participantId || participant.response === response || this.participantActions[participantId]) {
      return;
    }

    this.setParticipantAction(participantId, true);
    this.clearMessages();
    this.guestOwnerService.updateParticipant(this.selectedEvent.id, participantId, { response }).subscribe(
      (updatedParticipant) => {
        this.setParticipantAction(participantId, false);
        this.upsertParticipant(Object.assign({}, participant, updatedParticipant, {
          participantId: updatedParticipant.participantId || participantId
        }));
        this.successMsg = this.t("guestOwner.messages.responseSaved");
        this.finishAction();
      },
      (error) => this.handleParticipantError(error, participantId)
    );
  }

  public updateParticipantName(participant: GuestOwnerParticipantDto, name: string): void {
    const participantId = this.participantManagementId(participant);
    const publicParticipantId = participant && participant.playerId ? participant.playerId : participantId;
    if (!this.selectedEvent || !participant || !participantId || !name || this.participantActions[participantId]) {
      return;
    }

    if (!this.invite || !this.invite.inviteToken) {
      this.createInviteAndUpdateParticipantName(participant, name);
      return;
    }

    this.setParticipantAction(participantId, true);
    this.clearMessages();
    this.guestOwnerService.rsvp(this.invite.inviteToken, {
      participantId: publicParticipantId,
      name,
      response: participant.response || "COMING"
    }).subscribe(
      (updatedParticipant) => {
        this.setParticipantAction(participantId, false);
        this.upsertParticipant(Object.assign({}, participant, updatedParticipant, {
          participantId,
          playerId: updatedParticipant.playerId || publicParticipantId,
          matchOwner: participant.matchOwner,
          source: participant.source || updatedParticipant.source
        }));
        this.successMsg = this.t("guestOwner.playerNameSaved");
        this.finishAction();
      },
      (error) => this.handleParticipantError(error, participantId)
    );
  }

  public removeParticipant(participant: GuestOwnerParticipantDto): void {
    const participantId = this.participantManagementId(participant);
    if (!this.selectedEvent || !participant || !participantId || this.participantActions[participantId]) {
      return;
    }

    this.setParticipantAction(participantId, true);
    this.clearMessages();
    this.guestOwnerService.deleteParticipant(this.selectedEvent.id, participantId).subscribe(
      () => {
        this.setParticipantAction(participantId, false);
        this.participants = this.participants.filter((item) => this.participantManagementId(item) !== participantId);
        this.successMsg = this.t("guestOwner.messages.playerRemoved");
        this.finishAction();
      },
      (error) => this.handleParticipantError(error, participantId)
    );
  }

  public deleteMatch(): void {
    if (!this.selectedEvent || this.loading) {
      return;
    }

    this.confirmDeleteVisible = true;
  }

  public cancelDeleteMatch(): void {
    this.confirmDeleteVisible = false;
  }

  public confirmDeleteMatch(): void {
    if (!this.selectedEvent || this.loading) {
      return;
    }

    const eventId = this.selectedEvent.id;
    this.loading = true;
    this.confirmDeleteVisible = false;
    this.clearMessages();
    this.guestOwnerService.deleteEvent(eventId).subscribe(
      () => {
        this.loading = false;
        this.handleSelectedEventDeleted(eventId, this.t("guestOwner.messages.teamDeleted"));
      },
      (error) => this.handleError(error)
    );
  }

  public closeNotice(): void {
    this.noticeTitle = null;
    this.noticeMessage = null;
    if (!this.session && !this.selectedEvent) {
      this.returnToLanding();
    }
  }

  public participantTrackId(participant: GuestOwnerParticipantDto): string {
    if (!participant) {
      return "";
    }

    return this.participantManagementId(participant) || participant.playerId || participant.name || "";
  }

  public participantManagementId(participant: GuestOwnerParticipantDto): string {
    return participant && participant.participantId ? participant.participantId : "";
  }

  public participantActionInProgress(participant: GuestOwnerParticipantDto): boolean {
    const participantId = this.participantManagementId(participant);
    return participantId ? !!this.participantActions[participantId] : false;
  }

  public participantStatusLabel(participant: GuestOwnerParticipantDto): string {
    if (!participant || participant.response !== "NOT_COMING") {
      return participant && participant.matchOwner
        ? this.t("guestOwner.youAreComing")
        : this.t("guestOwner.coming");
    }

    return participant.matchOwner
      ? this.t("guestOwner.youAreNotComing")
      : this.t("guestOwner.notComing");
  }

  public selectedComingActionLabel(): string {
    if (!this.selectedParticipant || !this.selectedParticipant.matchOwner) {
      return this.t("guestOwner.coming");
    }

    return this.selectedParticipant.response !== "NOT_COMING"
      ? this.t("guestOwner.youAreComing")
      : this.t("guestOwner.iAmComing");
  }

  public selectedNotComingActionLabel(): string {
    if (!this.selectedParticipant || !this.selectedParticipant.matchOwner) {
      return this.t("guestOwner.notComing");
    }

    return this.selectedParticipant.response === "NOT_COMING"
      ? this.t("guestOwner.youAreNotComing")
      : this.t("events.pass");
  }

  public comingParticipants(): GuestOwnerParticipantDto[] {
    return this.participants.filter((participant) => participant.response !== "NOT_COMING");
  }

  public notComingParticipants(): GuestOwnerParticipantDto[] {
    return this.participants.filter((participant) => participant.response === "NOT_COMING");
  }

  public comingParticipantItems(): GuestParticipantListItem[] {
    return this.participantItems(this.comingParticipants());
  }

  public notComingParticipantItems(): GuestParticipantListItem[] {
    return this.participantItems(this.notComingParticipants(), true);
  }

  public openParticipantItem(item: GuestParticipantListItem): void {
    const participant = this.participants.find((entry) => this.participantTrackId(entry) === item.id);
    if (participant) {
      this.openParticipantSheet(participant);
    }
  }

  public participantInitial(participant: GuestOwnerParticipantDto): string {
    const name = this.participantDisplayName(participant);
    return name
      ? name.trim().charAt(0)
      : "?";
  }

  public participantDisplayName(participant: GuestOwnerParticipantDto): string {
    if (this.isDefaultGuestOwnerName(participant)) {
      return this.t("guestOwner.participants.organizer");
    }

    return participant && participant.name ? participant.name : "";
  }

  public participantDescription(participant: GuestOwnerParticipantDto): string {
    if (participant && participant.matchOwner === true) {
      return this.t("guestOwner.participants.organizer");
    }

    const source = participant && participant.source ? String(participant.source).toUpperCase() : "";
    if (source === "MANAGER") {
      return this.t("guestOwner.addedByManager");
    }

    if (source === "INVITE") {
      return this.t("publicInvite.participants.joinedFromInvite");
    }

    return this.t("guestOwner.participants.guestPlayer");
  }

  private participantItems(participants: GuestOwnerParticipantDto[], muted = false): GuestParticipantListItem[] {
    return participants.map((participant) => ({
      id: this.participantTrackId(participant),
      name: this.participantDisplayName(participant),
      initial: this.participantInitial(participant),
      description: this.participantDescription(participant),
      muted
    }));
  }

  private isDefaultGuestOwnerName(participant: GuestOwnerParticipantDto): boolean {
    const name = participant && participant.name ? participant.name.trim().toLocaleLowerCase() : "";
    return participant && participant.matchOwner === true && GuestOwnerComponent.DEFAULT_MANAGER_NAMES.has(name);
  }

  public selectedEventDate(): string {
    if (!this.selectedEvent || !this.selectedEvent.startDateTime) {
      return this.t("guestOwner.notAddedYet");
    }

    const start = new Date(this.selectedEvent.startDateTime);
    if (isNaN(start.getTime())) {
      return this.t("guestOwner.notAddedYet");
    }

    return start.toLocaleDateString(this.currentDateLocale(), { weekday: "long", day: "numeric", month: "short" });
  }

  public selectedEventStartTime(): string {
    if (!this.selectedEvent || !this.selectedEvent.startDateTime) {
      return "";
    }

    const start = new Date(this.selectedEvent.startDateTime);
    if (isNaN(start.getTime())) {
      return "";
    }

    return start.toLocaleTimeString(this.currentDateLocale(), { hour: "2-digit", minute: "2-digit" });
  }

  public selectedEventLocation(): string {
    return this.form.playSpaceName ||
      (this.selectedEvent && this.selectedEvent.locationName) ||
      (this.selectedEvent && this.selectedEvent.playSpace && this.selectedEvent.playSpace.name) ||
      this.t("guestOwner.notAddedYet");
  }

  public managerDisplayName(): string {
    return this.session && this.session.player
      ? (this.session.player.nickName || this.session.player.name || this.form.managerName)
      : this.form.managerName;
  }

  public chatMessageSender(message: PublicEventInviteChatMessageResponse): string {
    return message && message.senderName ? message.senderName : this.t("players.player");
  }

  public chatMessageText(message: PublicEventInviteChatMessageResponse): string {
    if (message && message.deleted) {
      return this.t("guestOwner.chat.deletedMessage");
    }

    return message && message.text ? message.text : "";
  }

  public chatMessageTime(message: PublicEventInviteChatMessageResponse): string {
    const timestamp = message && (message.createdAt || message.created);
    if (!timestamp) {
      return "";
    }

    const created = new Date(timestamp);
    if (isNaN(created.getTime())) {
      return "";
    }

    return created.toLocaleTimeString(this.currentDateLocale(), { hour: "2-digit", minute: "2-digit" });
  }

  public androidDownloadUrl(): string {
    if (!GuestOwnerComponent.ANDROID_APP_URL) return "";
    const referrer = new URLSearchParams({
      utm_source: "guest_manager",
      utm_medium: "web",
      utm_campaign: "match_manager",
      deep_link: this.matchLinkUrl()
    }).toString();

    return `${GuestOwnerComponent.ANDROID_APP_URL}&referrer=${encodeURIComponent(referrer)}`;
  }

  public iosDownloadUrl(): string {
    if (!GuestOwnerComponent.IOS_APP_URL) return "";
    const campaign = this.invite && this.invite.inviteToken
      ? `guest_manager_${this.invite.inviteToken}`
      : "guest_manager";
    const params = new URLSearchParams({
      ct: campaign,
      mt: "8"
    }).toString();

    return `${GuestOwnerComponent.IOS_APP_URL}?${params}`;
  }

  public matchLinkUrl(): string {
    const token = this.invite && this.invite.inviteToken ? this.invite.inviteToken : "";
    return nativeAppUrl(`join/${encodeURIComponent(token)}`);
  }

  public continueInMobileApp(): void {
    if (!this.selectedEvent || this.openingMobileApp) {
      return;
    }
    this.openingMobileApp = true;
    this.guestUpgradeClaimService.issueGuestOwnerContinuation(this.selectedEvent.id).subscribe(
      (handoff) => {
        this.openingMobileApp = false;
        this.startGuestContinuationObserver(this.selectedEvent.id);
        window.location.href = nativeAppUrl(`guest-continuation?handoff=${encodeURIComponent(handoff.handoffCode)}`);
      },
      () => {
        this.openingMobileApp = false;
        this.showNotice("publicInvite.appDownload.continueFailedTitle", "publicInvite.appDownload.continueFailed");
      }
    );
  }

  public openClaimedMatchInApp(): void {
    if (!this.selectedEvent || !this.selectedEvent.id) {
      return;
    }
    this.guestUpgradeClaimService.claimedMatchAppUrl("GUEST_OWNER", this.selectedEvent.id)
      .subscribe({
        next: (url) => window.location.href = url,
        error: () => this.showNotice("publicInvite.appDownload.continueFailedTitle", "publicInvite.appDownload.continueFailed")
      });
  }

  private startGuestContinuationObserver(eventId: string): void {
    this.stopGuestContinuationObserver();
    this.guestContinuationClaimed = this.guestUpgradeClaimService.continuationWasClaimed(
      "GUEST_OWNER",
      eventId
    );
    if (!this.guestUpgradeClaimService.hasContinuationObserver("GUEST_OWNER", eventId)) {
      return;
    }
    this.guestContinuationObserverError = false;
    this.guestContinuationObservation = this.guestContinuationObserverService
      .observe("GUEST_OWNER", eventId)
      .subscribe((observation) => {
        if (observation.error) {
          this.guestContinuationObserverError = true;
          return;
        }
        this.guestContinuationObserverError = false;
        if (observation.status && observation.status.status === "REVOKED") {
          this.guestContinuationClaimed = false;
          this.router.navigate(["/"]);
          return;
        }
        if (observation.status && observation.status.status === "CLAIMED") {
          this.guestContinuationClaimed = true;
        }
      });
  }

  public retryGuestContinuationObserver(): void {
    if (this.selectedEvent && this.selectedEvent.id) {
      if (this.restoringGuestContinuation) {
        this.restoreGuestContinuation(this.selectedEvent.id);
        return;
      }
      this.startGuestContinuationObserver(this.selectedEvent.id);
    }
  }

  private restoreGuestContinuation(eventId: string): void {
    // The guest credential may already be revoked by a merge. The observer
    // credential is the authority for recovery, not a cached success flag.
    this.restoringGuestContinuation = true;
    this.guestContinuationObserverError = false;
    this.selectedEvent = { id: eventId, name: this.session.eventName || "" } as GuestOwnerEventResponse;
    this.guestUpgradeClaimService.continuationStatus("GUEST_OWNER", eventId).subscribe({
      next: (status) => {
        if (this.destroyed) return;
        this.restoringGuestContinuation = false;
        if (status && status.status === "REVOKED") {
          this.guestUpgradeClaimService.clearAllTransitionState();
          this.router.navigate(["/"]);
        } else if (status && status.status === "CLAIMED") {
          this.guestContinuationClaimed = true;
          this.finishSetupProgress();
        } else {
          this.selectedEvent = null;
          this.loadCurrentEvent();
        }
      },
      error: () => {
        if (this.destroyed) return;
        this.guestContinuationObserverError = true;
        this.finishSetupProgress();
      }
    });
  }

  private stopGuestContinuationObserver(): void {
    if (this.guestContinuationObservation) {
      this.guestContinuationObservation.unsubscribe();
      this.guestContinuationObservation = null;
    }
  }

  private createInviteAndAddGuest(guestName: string, retried = false): void {
    if (!this.selectedEvent) {
      this.showNotice("guestOwner.notices.saveTeamFirstTitle", "guestOwner.notices.saveBeforePlayers");
      return;
    }

    this.loading = true;
    this.clearMessages();
    this.guestOwnerService.invite(this.selectedEvent.id).subscribe(
      (invite) => {
        this.invite = invite;
        this.inviteUrl = this.guestOwnerService.publicInviteUrl(invite);
        this.sessionService.saveInvite(invite.inviteToken, invite.invitePath);
        this.rsvpGuest(guestName, retried);
      },
      (error) => this.handleInviteCreateError(error)
    );
  }

  private createDraftEvent(teamName: string): void {
    const draftName = (teamName || this.t("events.newMatch")).trim();
    this.loading = true;
    this.beginSetupProgress();
    this.clearMessages();
    this.guestOwnerService.createEvent({
      teamName: draftName,
      name: draftName,
      maxPlayers: 10
    }).pipe(timeout(6000)).subscribe(
      (event) => {
        if (this.destroyed) {
          return;
        }
        this.events = [event];
        this.selectEvent(event);
        this.form.eventName = event.name || draftName;
        this.clearAutoCreateUrl();
        this.createInviteForDraft(event);
      },
      (error) => this.handleCreateDraftError(error)
    );
  }

  private createInviteForDraft(event: GuestOwnerEventResponse): void {
    this.guestOwnerService.invite(event.id).subscribe(
      (invite) => {
        if (this.destroyed) {
          return;
        }
        this.invite = invite;
        this.inviteUrl = this.guestOwnerService.publicInviteUrl(invite);
        this.sessionService.saveInvite(invite.inviteToken, invite.invitePath);
        this.finishAction();
      },
      () => {
        if (this.destroyed) {
          return;
        }
        this.finishAction();
        this.successMsg = this.t("guestOwner.messages.teamCreatedInviteLater");
      }
    );
  }

  private handleCreateDraftError(error: any): void {
    if (error && error.status === 409) {
      this.recoverCurrentEventAfterCreateConflict();
      return;
    }

    this.handleError(error);
  }

  private recoverCurrentEventAfterCreateConflict(): void {
    this.loading = true;
    this.beginSetupProgress();
    this.guestOwnerService.currentEvent().subscribe(
      (response) => {
        if (this.destroyed) {
          return;
        }
        this.loading = false;
        if (response && response.event) {
          this.clearMessages();
          this.events = [response.event];
          this.selectEvent(response.event);
          this.invite = response.invite || null;
          this.inviteUrl = this.invite ? this.guestOwnerService.publicInviteUrl(this.invite) : "";
          this.clearAutoCreateUrl();
          return;
        }

        this.showNotice("guestOwner.notices.existingTeamTitle", "guestOwner.notices.existingTeamMessage");
        this.finishSetupProgress();
      },
      () => {
        if (this.destroyed) {
          return;
        }
        this.loading = false;
        this.showNotice("guestOwner.notices.existingTeamTitle", "guestOwner.notices.existingTeamMessage");
        this.finishSetupProgress();
      }
    );
  }

  private rsvpGuest(guestName: string, retried = false): void {
    this.loading = true;
    this.clearMessages();
    this.guestOwnerService.rsvp(this.invite.inviteToken, { name: guestName, response: "COMING" }).subscribe(
      (participant) => {
        if (this.destroyed) {
          return;
        }
        this.form.guestName = "";
        this.successMsg = this.t("guestOwner.messages.playerAdded");
        this.upsertParticipant(Object.assign({}, participant, {
          participantId: participant.participantId || participant.playerId,
          playerId: participant.playerId || participant.participantId,
          matchOwner: false,
          source: participant.source || "INVITE"
        }));
        this.loadParticipants(this.selectedEvent.id);
        this.finishAction();
      },
      (error) => {
        if (error && error.status === 404 && !retried) {
          this.invite = null;
          this.inviteUrl = "";
          this.sessionService.clearInvite();
          this.createInviteAndAddGuest(guestName, true);
          return;
        }

        this.handleError(error);
      }
    );
  }

  private createInviteAndUpdateParticipantName(participant: GuestOwnerParticipantDto, name: string): void {
    if (!this.selectedEvent) {
      this.showNotice("guestOwner.notices.saveTeamFirstTitle", "guestOwner.notices.saveBeforePlayers");
      return;
    }

    this.loading = true;
    this.clearMessages();
    this.guestOwnerService.invite(this.selectedEvent.id).subscribe(
      (invite) => {
        this.invite = invite;
        this.inviteUrl = this.guestOwnerService.publicInviteUrl(invite);
        this.sessionService.saveInvite(invite.inviteToken, invite.invitePath);
        this.finishAction();
        this.updateParticipantName(participant, name);
      },
      (error) => this.handleInviteCreateError(error)
    );
  }

  private saveManagerName(): void {
    const managerName = (this.form.managerName || "").trim();
    if (!managerName) {
      this.showNotice("guestOwner.notices.missingNameTitle", "guestOwner.notices.managerDisplayNameRequired");
      return;
    }

    this.loading = true;
    this.clearMessages();
    this.guestOwnerService.updateMe({ name: managerName, managerName }).subscribe(
      (response) => {
        if (this.destroyed) {
          return;
        }

        const updatedPlayer = this.extractUpdatedManager(response, managerName);
        this.session = this.sessionService.updatePlayer(updatedPlayer) || Object.assign({}, this.session, { player: updatedPlayer });
        this.form.managerName = updatedPlayer.nickName || updatedPlayer.name || managerName;
        this.participants = this.participants.map((participant) => participant.matchOwner
          ? Object.assign({}, participant, { name: this.form.managerName })
          : participant);
        this.selectedParticipant = this.selectedParticipant && this.selectedParticipant.matchOwner
          ? Object.assign({}, this.selectedParticipant, { name: this.form.managerName })
          : this.selectedParticipant;
        this.successMsg = this.t("guestOwner.messages.managerNameSaved");
        this.finishAction();
      },
      (error) => this.handleError(error)
    );
  }

  private loadCurrentEvent(): void {
    this.loading = true;
    this.beginSetupProgress();
    this.guestOwnerService.currentEvent().pipe(timeout(6000)).subscribe(
      (response) => {
        if (this.destroyed) {
          return;
        }
        this.loading = false;
        if (!response || !response.event) {
          if (this.pendingDraftName) {
            this.createDraftEvent(this.pendingDraftName);
            return;
          }
          this.handleNoActiveGuestEvent();
          return;
        }

        this.events = [response.event];
        this.selectEvent(response.event);
        this.invite = response.invite || null;
        this.inviteUrl = this.invite ? this.guestOwnerService.publicInviteUrl(this.invite) : "";
        this.clearAutoCreateUrl();
      },
      (error) => {
        if (this.destroyed) {
          return;
        }
        this.loading = false;
        if (error && error.status === 404) {
          if (this.pendingDraftName) {
            this.createDraftEvent(this.pendingDraftName);
            return;
          }
          this.handleNoActiveGuestEvent();
          return;
        }

        this.autoCreateRequested = false;
        this.showNotice("guestOwner.notices.loadTeamFailedTitle", "guestOwner.notices.loadTimeoutMessage");
        this.finishSetupProgress();
      }
    );
  }

  private handleNoActiveGuestEvent(message?: string): void {
    this.events = [];
    this.clearGuestSessionAndReturnToSplash(message);
  }

  private currentTargetUrl(): string {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  private clearAutoCreateUrl(): void {
    if (this.destroyed) {
      return;
    }

    if (!this.autoCreateRequested && !this.pendingDraftName) {
      return;
    }

    this.autoCreateRequested = false;
    this.pendingDraftName = "";
    this.router.navigate(["/guest-manager"], { replaceUrl: true });
  }

  private loadParticipants(eventId: string): void {
    const sequence = ++this.participantsRequestSequence;
    const current = () => !this.destroyed && this.selectedEvent?.id === eventId
      && sequence === this.participantsRequestSequence;
    this.guestOwnerService.participants(eventId).pipe(timeout(15000)).subscribe(
      (participants) => {
        if (!current()) return;
        this.participants = this.normalizeParticipants(participants || []);
        this.participantsRealtimeError = false;
      },
      () => {
        if (current()) this.participantsRealtimeError = true;
      }
    );
  }

  @HostListener("window:online")
  public reconnectParticipants(): void {
    if (this.destroyed || !this.selectedEvent || this.guestContinuationClaimed || this.restoringGuestContinuation) return;
    this.loadParticipants(this.selectedEvent.id);
    this.startParticipantsStream(this.selectedEvent.id);
  }

  @HostListener("window:offline")
  public disconnectParticipants(): void {
    if (this.destroyed || !this.selectedEvent) return;
    this.stopParticipantsStream();
    this.participantsRealtimeError = true;
  }

  private loadChatMessages(eventId: string): void {
    this.chatMessagesLoading = true;
    this.chatMessagesUnavailable = false;
    this.guestOwnerService.guestOwnerChatMessages(eventId).subscribe(
      (messages) => {
        this.chatMessages = messages || [];
        this.chatMessagesLoading = false;
      },
      () => {
        this.chatMessages = [];
        this.chatMessagesUnavailable = true;
        this.chatMessagesLoading = false;
      }
    );
  }

  private startParticipantsStream(eventId: string): void {
    this.stopParticipantsStream();
    this.participantsRealtimeAlive = false;
    this.participantsRealtimeError = false;

    this.closeParticipantsStream = this.guestOwnerService.openParticipantsStream(
      eventId,
      (event) => {
        this.ngZone.run(() => this.handleParticipantsStreamEvent(eventId, event));
      },
      () => {
        this.ngZone.run(() => this.handleParticipantsStreamError(eventId));
      }
    );
  }

  private handleParticipantsStreamEvent(eventId: string, event: { type: string; data: any }): void {
    if (this.destroyed || !this.selectedEvent || this.selectedEvent.id !== eventId) {
      return;
    }

    if (event.type === "connected") {
      this.participantsRealtimeAlive = true;
      this.participantsRealtimeError = false;
      this.loadParticipants(eventId);
      return;
    }

    if (event.type === "participants.changed") {
      this.participantsRealtimeAlive = true;
      this.participantsRealtimeError = false;
      this.loadParticipants(eventId);
      return;
    }

    if (event.type === "event.deleted") {
      this.handleSelectedEventDeleted(eventId, this.t("guestOwner.messages.teamDeleted"));
      return;
    }

    if (event.type === "heartbeat") {
      this.participantsRealtimeAlive = true;
    }
  }

  private handleParticipantsStreamError(eventId: string): void {
    if (this.destroyed || !this.selectedEvent || this.selectedEvent.id !== eventId) {
      return;
    }

    this.participantsRealtimeAlive = false;
    this.participantsRealtimeError = true;
    this.participantsStreamReconnectTimer = window.setTimeout(() => {
      if (this.selectedEvent && this.selectedEvent.id === eventId) {
        this.startParticipantsStream(eventId);
      }
    }, 3000);
  }

  private stopParticipantsStream(): void {
    if (this.closeParticipantsStream) {
      this.closeParticipantsStream();
      this.closeParticipantsStream = null;
    }

    if (this.participantsStreamReconnectTimer) {
      window.clearTimeout(this.participantsStreamReconnectTimer);
      this.participantsStreamReconnectTimer = null;
    }

    this.participantsRealtimeAlive = false;
    this.participantsRealtimeError = false;
  }

  private handleSelectedEventDeleted(eventId: string, message: string): void {
    this.events = this.events.filter((event) => event.id !== eventId);
    this.clearGuestSessionAndReturnToSplash(message);
  }

  private clearGuestSessionAndReturnToSplash(message?: string): void {
    this.finishSetupProgress();
    this.stopParticipantsStream();
    this.selectedEvent = null;
    this.participants = [];
    this.chatMessages = [];
    this.chatMessagesLoading = false;
    this.chatMessagesUnavailable = false;
    this.invite = null;
    this.inviteUrl = "";
    this.participantActions = {};
    this.participantsRealtimeAlive = false;
    this.participantsRealtimeError = false;
    this.sessionService.clearSession();
    this.session = null;
    this.newEvent();
    if (message) {
      this.successMsg = message;
    }
    this.router.navigate(["/"]);
  }

  private buildEventRequest(): GuestOwnerEventRequest {
    const eventName = (this.form.eventName || "").trim();
    const playSpaceName = (this.form.playSpaceName || "").trim();
    if (!eventName) {
      this.showNotice("guestOwner.notices.missingTeamNameTitle", "guestOwner.notices.teamNameRequired");
      return null;
    }

    const request: GuestOwnerEventRequest = {
      name: eventName,
      teamName: eventName,
      maxPlayers: Number(this.form.maxPlayers) || 10,
      locationName: playSpaceName || null
    };

    this.syncStartDateTimeFromParts();
    if (this.form.startDateTime) {
      request.startDateTime = new Date(this.form.startDateTime).toISOString();
    } else if (this.editorMode === "time") {
      request.startDateTime = null;
    }

    return request;
  }

  private applyDateTimeToForm(value: string): void {
    const localDateTime = this.toDateTimeInputValue(value);
    this.form.startDateTime = localDateTime;
    this.form.startDate = localDateTime ? localDateTime.slice(0, 10) : "";
    this.form.startTime = localDateTime ? localDateTime.slice(11, 16) : "";
  }

  private syncStartDateTimeFromParts(): void {
    this.form.startDateTime = this.form.startDate && this.form.startTime
      ? `${this.form.startDate}T${this.form.startTime}`
      : "";
  }

  private localDateInputValue(date: Date): string {
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 10);
  }

  private toDateTimeInputValue(value: string): string {
    if (!value) {
      return "";
    }

    const date = new Date(value);
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
  }

  private clearMessages(): void {
    this.errorMsg = null;
    this.successMsg = null;
  }

  private beginSetupProgress(): void {
    this.setupInProgress = true;
    this.stopSetupWatchdog();
    this.setupWatchdogTimer = window.setTimeout(() => {
      if (this.destroyed || this.selectedEvent || !this.setupInProgress) {
        return;
      }

      this.loading = false;
      this.autoCreateRequested = false;
      this.setupInProgress = false;
      this.showNotice("guestOwner.notices.loadTimeoutTitle", "guestOwner.notices.loadTimeoutMessage");
    }, 8000);
  }

  private handleEventSaveError(error: any): void {
    if (error && error.status === 404) {
      this.finishAction();
      this.editorMode = null;
      this.showNotice("guestOwner.notices.teamNotFoundTitle", "guestOwner.notices.teamNotFoundMessage");
      this.loadCurrentEvent();
      return;
    }

    this.handleError(error);
  }

  private handleInviteCreateError(error: any): void {
    if (error && error.status === 404) {
      this.finishAction();
      this.showNotice("publicInvite.unavailableTitle", "guestOwner.notices.inviteUnavailableMessage");
      this.loadCurrentEvent();
      return;
    }

    this.handleError(error);
  }

  private extractUpdatedManager(response: any, fallbackName: string): GuestOwnerPlayerDto {
    const responsePlayer = response && response.player ? response.player : response;
    const currentPlayer = this.session && this.session.player ? this.session.player : null;
    return Object.assign({}, currentPlayer || {}, responsePlayer || {}, {
      name: responsePlayer && responsePlayer.name ? responsePlayer.name : fallbackName,
      nickName: responsePlayer && responsePlayer.nickName ? responsePlayer.nickName : fallbackName
    });
  }

  private showNotice(title: string, message: string): void {
    this.errorMsg = null;
    this.successMsg = null;
    this.noticeTitle = this.localizedText(title);
    this.noticeMessage = this.localizedText(message);
    this.finishAction();
  }

  private finishSetupProgress(): void {
    this.setupInProgress = false;
    this.stopSetupWatchdog();
  }

  private stopSetupWatchdog(): void {
    if (this.setupWatchdogTimer) {
      window.clearTimeout(this.setupWatchdogTimer);
      this.setupWatchdogTimer = null;
    }
  }

  private setParticipantAction(participantId: string, active: boolean): void {
    this.participantActions = Object.assign({}, this.participantActions, { [participantId]: active });
  }

  private upsertParticipant(updatedParticipant: GuestOwnerParticipantDto): void {
    const updatedParticipantKey = this.participantIdentityKey(updatedParticipant);
    const nextParticipants = this.participants.filter((participant) => this.participantIdentityKey(participant) !== updatedParticipantKey);
    nextParticipants.push(updatedParticipant);
    this.participants = this.normalizeParticipants(nextParticipants);
  }

  private normalizeParticipants(participants: GuestOwnerParticipantDto[]): GuestOwnerParticipantDto[] {
    const byParticipantId: {[participantId: string]: GuestOwnerParticipantDto} = {};
    participants.forEach((participant) => {
      const participantKey = this.participantIdentityKey(participant);
      if (participantKey) {
        byParticipantId[participantKey] = participant;
      }
    });
    return Object.keys(byParticipantId).map((participantId) => byParticipantId[participantId]);
  }

  private participantIdentityKey(participant: GuestOwnerParticipantDto): string {
    if (!participant) {
      return "";
    }

    return participant.participantId || participant.playerId || participant.name || "";
  }

  private handleParticipantError(error: any, participantId: string): void {
    this.setParticipantAction(participantId, false);
    this.loading = false;

    if (error && error.status === 403) {
      this.showNotice("guestOwner.notices.noAccessTitle", "guestOwner.notices.noAccessMessage");
      return;
    }

    if (error && error.status === 404) {
      this.showNotice("guestOwner.notices.playerNotFoundTitle", "guestOwner.notices.playerNotFoundMessage");
      if (participantId) {
        this.participants = this.participants.filter((participant) => this.participantManagementId(participant) !== participantId);
      }
      return;
    }

    if (error && error.status === 409) {
      if (error.error && error.error.code === "PARTICIPATION_CHANGED") {
        this.showNotice("common.error", "guestOwner.notices.participationChangedMessage");
        return;
      }
      this.showNotice("guestOwner.notices.teamFullTitle", "guestOwner.notices.teamFullMessage");
      return;
    }

    this.showNotice("guestOwner.notices.saveFailedTitle", error && error.error && error.error.message ? error.error.message : this.t("common.tryAgain"));
  }

  private handleError(error: any): void {
    if (this.destroyed) {
      return;
    }

    this.loading = false;
    this.autoCreateRequested = false;
    this.finishSetupProgress();
    if (error && error.error && error.error.code === "EVENT_MANAGER_CHANGED") {
      this.showNotice("guestOwner.notices.saveFailedTitle", "guestOwner.ownershipChanged");
      return;
    }
    this.showNotice("guestOwner.notices.saveFailedTitle", error && error.error && error.error.message ? error.error.message : this.t("common.tryAgain"));
  }

  private t(key: string, params?: any): string {
    return this.translate.instant(key, params);
  }

  private localizedText(value: string): string {
    if (!value) {
      return "";
    }

    if (value.indexOf("guestOwner.") === 0 || value.indexOf("common.") === 0 || value.indexOf("publicInvite.") === 0) {
      return this.t(value);
    }

    return value;
  }

  private currentDateLocale(): string {
    return this.translate.currentLang === "hr" ? "hr-HR" : "en-US";
  }

  private finishAction(): void {
    if (this.destroyed) {
      return;
    }

    this.loading = false;
  }
}
